import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { startOfWeek, endOfWeek } from 'date-fns';
import { getDateRange, parseErrorMessage } from './utils/helpers';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { useDataRefresh, useInvalidateData } from './hooks/useDataRefresh';
import Login from './components/Login';
import PublicProposalView from './components/PublicProposalView';
import ResetPassword from './components/ResetPassword';
import AppLayout from './components/AppLayout';
import Dashboard from './components/Dashboard';
import ContactManager from './components/ContactManager';
import ProposalManager from './components/ProposalManager';
import ProductManager from './components/ProductManager';
import CompanyManager from './components/CompanyManager';
import SettingsManager from './components/SettingsManager';
import ReportsManager from './components/ReportsManager';
import AuditManager from './components/AuditManager';
import FullOrderManager from './components/FullOrderManager';
import PurchaseOrderManager from './components/PurchaseOrderManager';
import CustomerOrderManager from './components/CustomerOrderManager';
import FollowUpManager from './components/FollowUpManager';
import { clientService } from './services/clientService';
import { interactionService } from './services/interactionService';
import { proposalService } from './services/proposalService';
import { productService } from './services/productService';
import { companyService } from './services/companyService';
import { userManagementService } from './services/userManagementService';
import { settingsService } from './services/settingsService';
import { weeklyGoals } from './mock/data';
import {
  mapClientToContact,
  mapInteractionToFormat,
  mapContactToClient,
  mapInteractionToAPI,
  mapProposalToFormat,
  mapProposalToAPI,
  mapProductToFormat,
  mapProductToAPI,
  mapCompanyToFormat,
  mapCompanyToAPI,
} from './utils/apiMapper';

const AppContent = () => {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const { showNotification } = useNotification();
  const [activeTab, setActiveTab] = useState('contacts');
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }
    return localStorage.getItem('theme') || 'light';
  });
  const [contactToExpand, setContactToExpand] = useState(null); // ID del contacto a expandir
  const [contactToEdit, setContactToEdit] = useState(null); // ID del contacto a editar directamente
  const [customerOrderToOpen, setCustomerOrderToOpen] = useState(null); // ID del pedido a abrir tras navegar
  const [contacts, setContacts] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contactInteractions, setContactInteractions] = useState([]);
  const [users, setUsers] = useState([]);
  const [goals, setGoals] = useState(weeklyGoals);
  const [quarterlyGoals, setQuarterlyGoals] = useState({
    q1: 0,
    q2: 0,
    q3: 0,
    q4: 0
  });
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('theme-dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  // ============================================
  // SISTEMA DE REFRESCO AUTOMÁTICO DE DATOS
  // ============================================
  // Este sistema permite que los datos se actualicen automáticamente cuando:
  // 1. Otros usuarios hacen cambios (polling cada 60 segundos)
  // 2. El usuario vuelve a la ventana del navegador
  // 3. El usuario cambia de pestaña dentro del CRM
  // 4. Se completa una operación CRUD (invalidación inmediata)
  
  // Funciones de refresco individuales para cada tipo de dato
  const refreshContacts = async () => {
    try {
      const contactsData = await clientService.getAll();
      if (contactsData && Array.isArray(contactsData)) {
        const mappedContacts = contactsData
          .map(mapClientToContact)
          .filter(contact => contact !== null && contact !== undefined);
        setContacts(mappedContacts);
      }
    } catch (error) {
      console.error('Error refrescando contactos:', error);
    }
  };

  const refreshProposals = async () => {
    try {
      const proposalsData = await proposalService.getAll();
      if (proposalsData && Array.isArray(proposalsData)) {
        const mappedProposals = proposalsData
          .map(mapProposalToFormat)
          .filter(proposal => proposal !== null && proposal !== undefined);
        setProposals(mappedProposals);
      }
    } catch (error) {
      console.error('Error refrescando propuestas:', error);
    }
  };

  const refreshProducts = async () => {
    try {
      const productsData = await productService.getAll();
      if (productsData && Array.isArray(productsData)) {
        const mappedProducts = productsData
          .map(mapProductToFormat)
          .filter(product => product !== null && product !== undefined);
        setProducts(mappedProducts);
      }
    } catch (error) {
      console.error('Error refrescando productos:', error);
    }
  };

  const refreshCompanies = async () => {
    try {
      const companiesData = await companyService.getAll();
      if (companiesData && Array.isArray(companiesData)) {
        const mappedCompanies = companiesData
          .map(mapCompanyToFormat)
          .filter(company => company !== null && company !== undefined);
        setCompanies(mappedCompanies);
      }
    } catch (error) {
      console.error('Error refrescando empresas:', error);
    }
  };

  // Hook de refresco automático con polling para datos principales
  // Polling de 60 segundos para pantallas críticas (propuestas)
  useDataRefresh(refreshProposals, {
    pollingInterval: 60000, // 60 segundos - solo para propuestas (pantalla crítica)
    refetchOnWindowFocus: true, // Refrescar cuando vuelve a la ventana
    refetchOnTabChange: true, // Refrescar cuando cambia de pestaña
    dependencies: [activeTab === 'proposals'], // Refrescar cuando entra a propuestas
    enabled: isAuthenticated, // Solo si está autenticado
  });

  // Refresco automático para otros datos (sin polling, solo on focus/change)
  useDataRefresh(refreshContacts, {
    pollingInterval: 0, // Sin polling
    refetchOnWindowFocus: true,
    refetchOnTabChange: true,
    dependencies: [activeTab === 'contacts'],
    enabled: isAuthenticated,
  });

  useDataRefresh(refreshProducts, {
    pollingInterval: 0,
    refetchOnWindowFocus: true,
    refetchOnTabChange: true,
    dependencies: [activeTab === 'products'],
    enabled: isAuthenticated,
  });

  useDataRefresh(refreshCompanies, {
    pollingInterval: 0,
    refetchOnWindowFocus: true,
    refetchOnTabChange: true,
    dependencies: [activeTab === 'companies'],
    enabled: isAuthenticated,
  });

  // Funciones de invalidación para llamar después de operaciones CRUD
  // Estas funciones refrescan inmediatamente los datos del backend
  const invalidateContacts = useInvalidateData(refreshContacts);
  const invalidateProposals = useInvalidateData(refreshProposals);
  const invalidateProducts = useInvalidateData(refreshProducts);
  const invalidateCompanies = useInvalidateData(refreshCompanies);

  // Cargar datos del API (carga inicial)
  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated]);

  const loadAllData = async () => {
    setLoadingData(true);
    // Cargar objetivos trimestrales desde el backend
    try {
      const quarterlyGoalsData = await settingsService.getQuarterlyGoals();
      setQuarterlyGoals(quarterlyGoalsData);
    } catch (error) {
      console.error('Error cargando objetivos trimestrales:', error);
    }
    try {
      // Cargar datos principales (no fallan si hay error)
      const contactsData = await clientService.getAll().catch(err => { 
        console.error('Error cargando clientes:', err); 
        return []; 
      });

      // Mapear datos del API al formato esperado
      if (!contactsData || !Array.isArray(contactsData)) {
        console.warn('⚠️ contactsData no es un array:', contactsData);
        setContacts([]);
      } else {
        const mappedContacts = (contactsData || [])
          .map(mapClientToContact)
          .filter(contact => contact !== null && contact !== undefined);
        setContacts(mappedContacts);
      }
      
      // Cargar propuestas
      try {
        const proposalsData = await proposalService.getAll();
        if (!proposalsData || !Array.isArray(proposalsData)) {
          setProposals([]);
        } else {
          // CRÍTICO: Filtrar nulls después del mapeo para evitar errores en filtros posteriores
          const mappedProposals = proposalsData
            .map(mapProposalToFormat)
            .filter(proposal => proposal !== null && proposal !== undefined);
          setProposals(mappedProposals);
        }
      } catch (error) {
        console.error('Error cargando propuestas:', error);
        setProposals([]);
      }

      // Cargar productos
      try {
        const productsData = await productService.getAll();
        if (!productsData || !Array.isArray(productsData)) {
          setProducts([]);
        } else {
          // CRÍTICO: Filtrar nulls después del mapeo para evitar errores en filtros posteriores
          const mappedProducts = (productsData || [])
            .map(mapProductToFormat)
            .filter(product => product !== null && product !== undefined);
          setProducts(mappedProducts);
        }
      } catch (error) {
        console.error('Error cargando productos:', error);
        setProducts([]);
      }

      // Cargar empresas
      try {
        const companiesData = await companyService.getAll();
        if (!companiesData || !Array.isArray(companiesData)) {
          setCompanies([]);
        } else {
          // CRÍTICO: Filtrar nulls después del mapeo para evitar errores en filtros posteriores
          const mappedCompanies = (companiesData || [])
            .map(mapCompanyToFormat)
            .filter(company => company !== null && company !== undefined);
          setCompanies(mappedCompanies);
        }
      } catch (error) {
        console.error('Error cargando empresas:', error);
        setCompanies([]);
      }

      // Cargar usuarios
      try {
        const usersData = await userManagementService.getAll();
        setUsers(usersData || []);
      } catch (error) {
        console.warn('Error cargando usuarios:', error);
        setUsers([]);
      }

      // Cargar interacciones para cada contacto
      const interactionsArray = [];
      for (const contact of contactsData || []) {
        try {
          const interactions = await interactionService.getByContactId(contact.id);
          // CRÍTICO: Filtrar nulls después del mapeo para evitar errores
          const mappedInteractions = (interactions || [])
            .map(mapInteractionToFormat)
            .filter(interaction => interaction !== null && interaction !== undefined);
          interactionsArray.push(...mappedInteractions);
        } catch (error) {
          console.error(`Error cargando interacciones para contacto ${contact.id}:`, error);
        }
      }
      // Convertir array a formato esperado por ContactManager
      setContactInteractions(interactionsArray);
    } catch (error) {
      console.error('Error cargando datos:', error);
      showNotification('No se pudieron cargar los datos. Verificá que el servidor esté disponible.', 'error');
    } finally {
      setLoadingData(false);
    }
  };

  // Handlers para Contacts/Clients
  const handleAddContact = async (newContact) => {
    try {
      const apiData = mapContactToClient(newContact);
      const created = await clientService.create(apiData);
      const mapped = mapClientToContact(created);
      // CRÍTICO: Validar que el mapeo no devolvió null antes de agregar
      if (mapped) {
        setContacts(prev => [...prev, mapped]);
      }
      // Invalidar datos para refrescar desde el backend (otros usuarios verán el cambio)
      await invalidateContacts();
      // Los goals se calculan automáticamente en calculatedGoals, no necesitamos actualizarlos manualmente
      return mapped;
    } catch (error) {
      console.error('Error creando cliente:', error);
      // Si el error es de autenticación, no mostrar alert ni lanzar
      if (error?.isAuthError || error?.name === 'AuthenticationError') {
        return null;
      }
      showNotification(`No se pudo guardar el contacto. ${parseErrorMessage(error, 'Por favor revisá los datos e intentá de nuevo.')}`, 'error');
      throw error;
    }
  };

  const handleImportContacts = async (contactsToImport) => {
    try {
      const createdContacts = [];
      for (const contact of contactsToImport) {
        try {
          const apiData = mapContactToClient(contact);
          const created = await clientService.create(apiData);
          const mapped = mapClientToContact(created);
          // CRÍTICO: Validar que el mapeo no devolvió null antes de agregar
          if (mapped) {
            createdContacts.push(mapped);
          }
        } catch (error) {
          console.error('Error creando contacto:', error);
        }
      }
      setContacts(prev => [...prev, ...createdContacts]);
      // Invalidar datos para refrescar desde el backend
      await invalidateContacts();
      // Los goals se calculan automáticamente en calculatedGoals, no necesitamos actualizarlos manualmente
      return createdContacts;
    } catch (error) {
      console.error('Error importando contactos:', error);
      throw error;
    }
  };

  const handleUpdateContact = async (contactId, updates) => {
    try {
      // Obtener el contacto actual para combinar con los updates
      const currentContact = contacts.find(c => c.id === contactId);
      if (!currentContact) {
        throw new Error('Contacto no encontrado');
      }
      
      // Combinar datos actuales con los updates
      const mergedData = { ...currentContact, ...updates };
      const apiData = mapContactToClient(mergedData);
      const updated = await clientService.update(parseInt(contactId), apiData);
      
      // Validar respuesta antes de mapear
      if (!updated) {
        console.error('handleUpdateContact: respuesta del servidor es null o undefined');
        throw new Error('El servidor no devolvió datos actualizados');
      }
      
      const mapped = mapClientToContact(updated);
      
      // Validar que el mapeo fue exitoso
      if (!mapped) {
        console.error('handleUpdateContact: mapClientToContact devolvió null', updated);
        throw new Error('Error al procesar los datos actualizados del servidor');
      }
      
      // CRÍTICO: Validar que el mapeo no devolvió null antes de actualizar
      if (mapped) {
        setContacts(prev =>
          prev
            .filter(contact => contact !== null && contact !== undefined) // Filtrar nulls existentes
            .map(contact =>
              contact.id === contactId ? mapped : contact
            )
        );
      }
      // Invalidar datos para refrescar desde el backend
      await invalidateContacts();
      return mapped;
    } catch (error) {
      console.error('Error actualizando cliente:', error);
      console.error('ContactId:', contactId);
      console.error('Updates:', updates);
      // Mostrar error más amigable al usuario
      showNotification(`No se pudo actualizar el contacto. ${parseErrorMessage(error, 'Por favor revisá los datos e intentá de nuevo.')}`, 'error');
      throw error;
    }
  };

  const handleContactEditHandled = useCallback(() => setContactToEdit(null), []);

  const handleDeleteContact = async (contactId) => {
    try {
      await clientService.delete(parseInt(contactId));
      setContacts(prev => prev.filter(contact => contact.id !== contactId));
      await invalidateContacts();
    } catch (error) {
      console.error('Error eliminando contacto:', error);
      showNotification(`No se pudo eliminar el contacto. ${parseErrorMessage(error)}`, 'error');
      throw error;
    }
  };

  // Handlers para Interactions
  const handleAddContactInteraction = async (newInteraction) => {
    try {
      const apiData = mapInteractionToAPI(newInteraction);
      const created = await interactionService.create(apiData);
      const mapped = mapInteractionToFormat(created);
      // CRÍTICO: Validar que el mapeo no devolvió null antes de agregar
      if (mapped) {
        // Asegurar que contactInteractions sea un array
        setContactInteractions(prev => Array.isArray(prev) ? [...prev, mapped] : [mapped]);
      }
      return mapped;
    } catch (error) {
      console.error('Error creando interacción:', error);
      // Si el error es de autenticación, no lanzarlo ya que el evento ya se disparó
      if (error?.isAuthError || error?.name === 'AuthenticationError' || 
          error?.message?.includes('sesión ha expirado') || 
          error?.message?.includes('session has expired')) {
        // El evento auth:token-expired ya se disparó, solo retornar sin hacer nada más
        return null;
      }
      // Para otros errores, mostrar mensaje y lanzar
      showNotification(`No se pudo registrar la interacción. ${parseErrorMessage(error)}`, 'error');
      throw error;
    }
  };


  const handleUpdateGoals = (newGoals) => {
    setGoals(newGoals);
  };

  // Handlers para Proposals
  const handleAddProposal = async (newProposal) => {
    try {
      const apiData = mapProposalToAPI(newProposal);
      const created = await proposalService.create(apiData);
      const mapped = mapProposalToFormat(created);
      // CRÍTICO: Validar que el mapeo no devolvió null antes de agregar
      if (mapped) {
        setProposals(prev => [...prev, mapped]);
      }
      // Invalidar datos para refrescar desde el backend (otros usuarios verán el cambio)
      await invalidateProposals();
      return mapped;
    } catch (error) {
      console.error('Error creando propuesta:', error);
      throw error;
    }
  };

  // Función específica para actualizar solo el campo proforma usando PATCH
  const handleUpdateProposalProforma = async (proposalId, proformaValue) => {
    try {
      
      // Usar PATCH para actualización parcial - solo enviar el campo proforma
      const updated = await proposalService.partialUpdate(parseInt(proposalId), {
        proforma: proformaValue
      });
      
      console.log('Respuesta del servidor (PATCH):', updated);
      
      const mapped = mapProposalToFormat(updated);
      
      // Actualizar el estado local
      if (mapped) {
        setProposals(prev =>
          prev
            .filter(proposal => proposal !== null && proposal !== undefined)
            .map(proposal =>
              proposal.id === proposalId ? mapped : proposal
            )
        );
      }
      
      // Invalidar datos para refrescar desde el backend
      await invalidateProposals();
      return mapped;
    } catch (error) {
      console.error('=== ERROR EN handleUpdateProposalProforma ===');
      console.error('Error completo:', error);
      console.error('Mensaje de error:', error?.message);
      console.error('Respuesta del servidor:', error?.response);
      console.error('Datos del error:', error?.response?.data);
      throw error;
    }
  };

  const handleUpdateProposal = async (proposalId, updates) => {
    console.log('=== handleUpdateProposal llamado ===');
    console.log('ID de propuesta:', proposalId);
    // No hacer JSON.stringify aquí porque updates puede contener referencias circulares
    console.log('Updates recibidos (ya mapeados al formato API)');
    try {
      const currentProposal = proposals.find(p => p.id === proposalId);
      if (!currentProposal) {
        throw new Error('Cotización no encontrada');
      }
      
      // Si updates ya viene mapeado desde mapProposalToAPI (tiene contact_id o items), usarlo directamente
      let apiData;
      if (updates.contact_id !== undefined || updates.items !== undefined) {
        // Ya está en formato API, usar directamente
        apiData = updates;
      } else {
        // Crear objeto limpio sin referencias circulares
        const cleanUpdates = {
          id: updates.id,
          proposalId: updates.proposalId,
          contactId: updates.contactId,
          companyId: updates.companyId,
          status: updates.status,
          proforma: updates.proforma,
          greeting: updates.greeting,
          salutation: updates.salutation,
          commercial_note: updates.commercial_note,
          stage: updates.stage,
          paymentTerms: updates.paymentTerms,
          rebate: updates.rebate,
          priceListId: updates.priceListId,
          expiryDate: updates.expiryDate,
          sentDate: updates.sentDate,
          approvedDate: updates.approvedDate,
          invoiceFile: updates.invoiceFile,
          items: updates.items,
          totalAmount: updates.totalAmount,
        };
        
        const mergedData = { ...currentProposal, ...cleanUpdates };
        apiData = mapProposalToAPI(mergedData);
      }
      console.log('Enviando PUT a /proposals/' + parseInt(proposalId));
      
      const updated = await proposalService.update(parseInt(proposalId), apiData);
      console.log('Respuesta del servidor:', updated);
      
      const mapped = mapProposalToFormat(updated);
      
      // CRÍTICO: Validar que el mapeo no devolvió null antes de actualizar
      if (mapped) {
        setProposals(prev =>
          prev
            .filter(proposal => proposal !== null && proposal !== undefined) // Filtrar nulls existentes
            .map(proposal =>
              proposal.id === proposalId ? mapped : proposal
            )
        );
      }
      // Invalidar datos para refrescar desde el backend
      await invalidateProposals();
      return mapped;
    } catch (error) {
      console.error('Error actualizando propuesta:', error);
      throw error;
    }
  };

  const handleDeleteProposal = async (proposalId) => {
    try {
      await proposalService.delete(parseInt(proposalId));
      setProposals(prev => prev.filter(proposal => proposal.id !== proposalId));
      // Invalidar datos para refrescar desde el backend
      await invalidateProposals();
    } catch (error) {
      console.error('Error eliminando propuesta:', error);
      throw error;
    }
  };

  // Handlers para Products
  const handleAddProduct = async (newProduct) => {
    try {
      const apiData = mapProductToAPI(newProduct);
      const created = await productService.create(apiData);
      const mapped = mapProductToFormat(created);
      // CRÍTICO: Validar que el mapeo no devolvió null antes de agregar
      if (mapped) {
        setProducts(prev => [...prev, mapped]);
      }
      // Invalidar datos para refrescar desde el backend
      await invalidateProducts();
      return mapped;
    } catch (error) {
      console.error('Error creando producto:', error);
      showNotification(`No se pudo guardar el producto. ${parseErrorMessage(error, 'Revisá los datos obligatorios e intentá de nuevo.')}`, 'error');
      throw error;
    }
  };

  const handleUpdateProduct = async (productId, updates) => {
    try {
      const currentProduct = products.find(p => p.id === productId);
      if (!currentProduct) {
        throw new Error('Producto no encontrado');
      }
      const mergedData = { ...currentProduct, ...updates };
      const apiData = mapProductToAPI(mergedData);
      const updated = await productService.update(parseInt(productId), apiData);
      const mapped = mapProductToFormat(updated);
      // CRÍTICO: Validar que el mapeo no devolvió null antes de actualizar
      if (mapped) {
        setProducts(prev =>
          prev
            .filter(product => product !== null && product !== undefined) // Filtrar nulls existentes
            .map(product =>
              product.id === productId ? mapped : product
            )
        );
      }
      // Invalidar datos para refrescar desde el backend
      await invalidateProducts();
      return mapped;
    } catch (error) {
      console.error('Error actualizando producto:', error);
      showNotification(`No se pudo actualizar el producto. ${parseErrorMessage(error, 'Revisá los datos obligatorios e intentá de nuevo.')}`, 'error');
      throw error;
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await productService.delete(parseInt(productId));
      setProducts(prev => prev.filter(product => product.id !== productId));
      // Invalidar datos para refrescar desde el backend
      await invalidateProducts();
    } catch (error) {
      console.error('Error eliminando producto:', error);
      showNotification(`No se pudo eliminar el producto. ${parseErrorMessage(error)}`, 'error');
      throw error;
    }
  };

  // Handlers para Companies
  const handleAddCompany = async (newCompany) => {
    try {
      const apiData = mapCompanyToAPI(newCompany);
      const created = await companyService.create(apiData);
      
      // Validar respuesta antes de mapear
      if (!created) {
        console.error('handleAddCompany: respuesta del servidor es null o undefined');
        throw new Error('El servidor no devolvió datos de la empresa creada');
      }
      
      const mapped = mapCompanyToFormat(created);
      
      // Validar que el mapeo fue exitoso
      if (!mapped) {
        console.error('handleAddCompany: mapCompanyToFormat devolvió null', created);
        throw new Error('Error al procesar los datos de la empresa creada');
      }
      
      setCompanies(prev => [...prev, mapped]);
      // Invalidar datos para refrescar desde el backend
      await invalidateCompanies();
      return mapped;
    } catch (error) {
      console.error('Error creando empresa:', error);
      showNotification(`No se pudo guardar la empresa. ${parseErrorMessage(error, 'Revisá los datos obligatorios e intentá de nuevo.')}`, 'error');
      throw error;
    }
  };

  const handleUpdateCompany = async (companyId, updates) => {
    try {
      const currentCompany = companies.find(c => c.id === companyId);
      if (!currentCompany) {
        throw new Error('Empresa no encontrada');
      }
      const mergedData = { ...currentCompany, ...updates };
      const apiData = mapCompanyToAPI(mergedData);
      const updated = await companyService.update(parseInt(companyId), apiData);
      
      // Validar respuesta antes de mapear
      if (!updated) {
        console.error('handleUpdateCompany: respuesta del servidor es null o undefined');
        throw new Error('El servidor no devolvió datos actualizados');
      }
      
      const mapped = mapCompanyToFormat(updated);
      
      // Validar que el mapeo fue exitoso
      if (!mapped) {
        console.error('handleUpdateCompany: mapCompanyToFormat devolvió null', updated);
        throw new Error('Error al procesar los datos actualizados del servidor');
      }
      
      setCompanies(prev =>
        prev.map(company =>
          company.id === companyId ? mapped : company
        )
      );
      // Invalidar datos para refrescar desde el backend
      await invalidateCompanies();
      return mapped;
    } catch (error) {
      console.error('Error actualizando empresa:', error);
      console.error('CompanyId:', companyId);
      console.error('Updates:', updates);
      showNotification(`No se pudo actualizar la empresa. ${parseErrorMessage(error, 'Revisá los datos obligatorios e intentá de nuevo.')}`, 'error');
      throw error;
    }
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await companyService.delete(parseInt(companyId));
      setCompanies(prev => prev.filter(company => company.id !== companyId));
      // Invalidar datos para refrescar desde el backend
      await invalidateCompanies();
    } catch (error) {
      console.error('Error eliminando empresa:', error);
      showNotification(`No se pudo eliminar la empresa. ${parseErrorMessage(error)}`, 'error');
      throw error;
    }
  };

  const [dateFilter, setDateFilter] = useState('currentWeek');

  const calculatedGoals = React.useMemo(() => {
    // Obtener el rango de fechas según el filtro seleccionado
    const { start: dateStart, end: dateEnd } = getDateRange(dateFilter);

    // 1. Reuniones: Contar interacciones de tipo 'Reunión' en el rango de fechas
    const weeklyMeetings = Array.isArray(contactInteractions) 
      ? contactInteractions.filter(interaction => {
          const interactionDate = interaction.date instanceof Date 
            ? interaction.date 
            : new Date(interaction.date);
          return interaction.type === 'Reunión' && 
                 interactionDate >= dateStart && 
                 interactionDate <= dateEnd;
        }).length
      : 0;

    // 2. Contactos: Contar contactos creados en el rango de fechas
    // CRÍTICO: Filtrar nulls y validar createdAt antes de acceder
    const weeklyContacts = contacts
      .filter(contact => contact !== null && contact !== undefined && contact.createdAt)
      .filter(contact => {
        const createdDate = contact.createdAt instanceof Date 
          ? contact.createdAt 
          : (contact.createdAt ? new Date(contact.createdAt) : new Date());
        return createdDate >= dateStart && createdDate <= dateEnd;
      }).length;

    // 3. Nuevos Leads: Contar contactos con status 'Lead' creados en el rango de fechas
    // CRÍTICO: Filtrar nulls y validar createdAt antes de acceder
    const weeklyNewLeads = contacts
      .filter(contact => contact !== null && contact !== undefined && contact.createdAt)
      .filter(contact => {
        const createdDate = contact.createdAt instanceof Date 
          ? contact.createdAt 
          : (contact.createdAt ? new Date(contact.createdAt) : new Date());
        return contact.status === 'Borrador' && 
               createdDate >= dateStart && 
               createdDate <= dateEnd;
      }).length;

    return {
      meetings: { ...goals.meetings, current: weeklyMeetings },
      contacts: { ...goals.contacts, current: weeklyContacts },
      leads: { ...goals.leads, current: weeklyNewLeads }
    };
  }, [contactInteractions, contacts, goals, dateFilter]);

  // Mostrar login si no está autenticado
  if (!isAuthenticated) {
    return <Login theme={theme} />;
  }

  if (loading || loadingData) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-slate-300' : 'text-gray-600'}>Cargando...</p>
        </div>
      </div>
    );
  }

  const renderActiveComponent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <Dashboard 
            goals={calculatedGoals} 
            proposals={proposals}
            products={products}
            quarterlyGoals={quarterlyGoals}
            onFilterChange={setDateFilter}
            dateFilter={dateFilter}
          />
        );
      case 'contacts':
        return (
          <ContactManager 
            contacts={contacts}
            companies={companies}
            contactInteractions={contactInteractions}
            contactToEdit={contactToEdit}
            onContactEditHandled={handleContactEditHandled}
            onAddContact={handleAddContact}
            onUpdateContact={handleUpdateContact}
            onDeleteContact={handleDeleteContact}
            onAddInteraction={handleAddContactInteraction}
            onImportContacts={handleImportContacts}
            contactToExpand={contactToExpand}
          />
        );
            case 'proposals':
              return (
                <ProposalManager
                  proposals={proposals}
                  contacts={contacts}
                  theme={theme}
                  onAddProposal={handleAddProposal}
                  onUpdateProposal={handleUpdateProposal}
                  onUpdateProposalProforma={handleUpdateProposalProforma}
                  onDeleteProposal={handleDeleteProposal}
                  onOpenCustomerOrder={async (orderId) => {
                    await refreshProposals();
                    setCustomerOrderToOpen(orderId);
                    setActiveTab('customer-orders');
                  }}
                />
              );
            case 'products':
              return (
                <ProductManager
                  products={products}
                  onAddProduct={handleAddProduct}
                  onUpdateProduct={handleUpdateProduct}
                  onDeleteProduct={handleDeleteProduct}
                />
              );
            case 'companies':
              return (
                <CompanyManager
                  companies={companies}
                  contacts={contacts}
                  onQuickEditContact={(contact) => {
                    setContactToExpand(contact.id);
                    setContactToEdit(contact.id);
                    setActiveTab('contacts');
                  }}
                  onAddCompany={handleAddCompany}
                  onUpdateCompany={handleUpdateCompany}
                  onDeleteCompany={handleDeleteCompany}
                />
              );
            case 'reports':
              return (
                <ReportsManager
                  contacts={contacts}
                  companies={companies}
                  contactInteractions={contactInteractions}
                  proposals={proposals}
                  products={products}
                />
              );
            case 'settings':
              return (
                <SettingsManager
                  goals={goals} 
                  onUpdateGoals={handleUpdateGoals}
                  quarterlyGoals={quarterlyGoals}
                  onUpdateQuarterlyGoals={setQuarterlyGoals}
                  onImportContacts={handleAddContact}
                  contacts={contacts}
                />
              );
            case 'customer-orders':
              return (
                <CustomerOrderManager
                  theme={theme}
                  contacts={contacts}
                  orderToOpen={customerOrderToOpen}
                  onOrderOpened={() => setCustomerOrderToOpen(null)}
                />
              );
            case 'full-orders':
              return <FullOrderManager theme={theme} contacts={contacts} />;
            case 'purchase-orders':
              return <PurchaseOrderManager theme={theme} contacts={contacts} />;
            case 'follow-up':
              return <FollowUpManager />;
            case 'audit':
              return <AuditManager />;
            default:
              return null;
    }
  };

  return (
    <AppLayout
      activeTab={activeTab}
      onTabChange={setActiveTab}
      user={user}
      onLogout={logout}
      theme={theme}
      onToggleTheme={() => setTheme(prev => prev === 'dark' ? 'light' : 'dark')}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Contenido de la sección activa */}
        {renderActiveComponent()}
      </motion.div>
    </AppLayout>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Ruta pública para propuestas compartidas - NO requiere autenticación */}
        <Route path="/proposal/shared/:token" element={<PublicProposalView />} />
        
        {/* Ruta para reset de contraseña - NO requiere autenticación */}
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Ruta principal de la aplicación - requiere autenticación */}
        <Route path="*" element={
          <NotificationProvider>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </NotificationProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
